import { useRef } from 'react';
import { useFormikContext } from 'formik';
import { pick } from 'lodash';
import { toast } from '@dashboard/context/ToastContext';
import useProduct from './useProduct';
import { toProductPayload } from './lib/productPayload';

/**
 * Submit only the named product fields, leaving everything else — most
 * importantly `prices` and `defaultPriceId` — out of the payload.
 * ProductService.update merges provided keys only, so a scoped submit can
 * never commit or clobber an in-flight price row (an open add-price draft or
 * a price being edited). This is what keeps the product title and the prices
 * layout fully usable while a price form is open.
 *
 * Values are read at call time through a live ref (a debounced caller's
 * closure would otherwise see stale Formik values). For state set in the same
 * tick — where even the ref lags `setFieldValue` — pass the new values via
 * `overrides`.
 *
 * Edit flow only: the create flow persists everything through its single
 * "Create checkout" submit.
 */
export default function useScopedProductSubmit() {
  const { values } = useFormikContext();
  const { updateProduct } = useProduct();

  const valuesRef = useRef(values);
  valuesRef.current = values;

  return async function submitScoped(keys, overrides = {}) {
    // Formik keeps picker-layout fields flat; the API stores them nested
    // under `pricePicker` — nest at this boundary like the full submit does.
    const payload = toProductPayload({ ...pick(valuesRef.current, keys), ...overrides });
    try {
      const { error } = await updateProduct.mutateAsync(payload);
      if (error) {
        toast({ title: error, type: 'error' });
      }
    } catch (err) {
      toast({ title: err?.message || 'Could not save changes', type: 'error' });
    }
  };
}
